import { useParams, Link, useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { usePipelineNode } from '../../hooks/usePipelineNode';
import { usePipelineNodes } from '../../hooks/usePipelineNodes';
import { usePipelineNodeRelationships, type PipelineNodeRelationship } from '../../hooks/usePipelineNodeRelationships';
import { pipelineNodeRelationshipService } from '../../services/pipelineNodeRelationship.service';
import Editor, { type Monaco } from '@monaco-editor/react';
import { useTheme } from '../../contexts/ThemeContext';
import { NodeDetailSidebar } from './NodeDetailSidebar';
import { getPipelineNodeIcon } from '../../components/PipelineNodeIcon';
import { BasicInfoTab } from './BasicInfoTab';
import { SourcesColumnsTab } from './SourcesColumnsTab';
import { ConnectionsTab } from './ConnectionsTab';
import { NodeDataTable } from '../../components/NodeDataTable';
import { RebuildModal } from '../../components/RebuildModal';
import { RelationshipsTab } from './RelationshipsTab';
import { ReportingPeriodsTab } from './ReportingPeriodsTab';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Loader2, Database, Layers, Lock, Save, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Search, Calculator, Link2, Settings, Sparkles, GripVertical, X, Key, Plus, Copy, Trash2, BarChart3, GitBranch, RefreshCw, AlertTriangle, MoreVertical } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Combobox, type ComboboxOption } from '../../components/ui/combobox';
import { MultiSelectCombobox, type MultiSelectComboboxOption } from '../../components/ui/multi-select-combobox';
import { useAvailableSnowflakeTables } from '../../hooks/useSnowflakeTables';
import { useSourceColumns } from '../../hooks/useSourceColumns';
import { useSources } from '../../hooks/useSource';
import { pipelineNodeService, type PipelineNode, type PipelineNodeMapOption, type PipelineNodeField, type DedupeRule } from '../../services/pipelineNode.service';
import { useQueryClient } from '@tanstack/react-query';
import { toastService } from '../../services/toast.service';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import ApiService from '../../services/api.service';
import { formatPercentage } from '../../services/formatting.service';
import { ColumnLineageGraph, type ColumnLineageGraph as ColumnLineageGraphType } from '../../components/ColumnLineageGraph';
import { FullScreenTakeover } from '../../components/FullScreenTakeover';
import { useColumnValueDistribution } from '../../hooks/useColumnValueDistribution';
import { useTemplateName } from '../../hooks/useTemplates';
import { useIsInDraftMode } from '../../hooks/useDraftMode';
import { LockedNodeOverlay } from '../../components/templates/LockedOverlay';
import {
  getDeleteFieldResult,
  updateDenormalizedFieldOnRelationship,
  removeDenormalizedFieldFromRelationship,
  removeFieldsFromList,
  prepareRelationshipSaveOperations,
  isPendingRelationshipId,
  getPendingFieldKey,
  preparePendingRelationshipForSave,
} from '../../utils/relationshipFieldUtils';
import { generateForeignKeyName } from '../../utils/foreignKeyUtils';
import { FilterTable, type FilterEntry } from '../../components/FilterTable';
import type { DataWhitelist, DataWhitelistEntry } from '../../components/UpstreamFilterTable';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DndContext,
  closestCenter,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  useDndMonitor,
} from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EditColumnDialog, type FieldFormData } from './EditColumnDialog';

// Helper function to count mapped columns for a field
function countMappedColumns(field: NonNullable<PipelineNode['fields']>[0]): number {
  if (!field.map_options || field.map_options.length === 0) {
    return 0;
  }
  
  return field.map_options.reduce((count, option) => {
    // If there are sub_options, count those instead of the parent
    if (option.sub_options && option.sub_options.length > 0) {
      return count + option.sub_options.filter(
        (sub) => sub.source_node_id && (sub.attribute_key || sub.attribute_id)
      ).length;
    }
    // Otherwise, count the option itself if it's a valid mapping
    // A valid mapping needs source_node_id and either attribute_key or attribute_id
    if (option.source_node_id && (option.attribute_key || option.attribute_id)) {
      return count + 1;
    }
    return count;
  }, 0);
}

// Helper function to get the display name for a field
function getFieldName(
  field: NonNullable<PipelineNode['fields']>[0],
  getRelationship?: (relationshipId: string) => any,
  getNodeName?: (nodeId: string) => string | undefined,
  currentNodeId?: string
): string {
  if (field.type === 'FOREIGN_KEY' && (field as PipelineNodeField).relationship_id && getRelationship && getNodeName && currentNodeId) {
    const relationship = getRelationship((field as PipelineNodeField).relationship_id!);
    if (relationship) {
      // Use relationship name if it exists, otherwise show "Connection to [parent node]"
      if (relationship.name) {
        return relationship.name;
      }
      // Current node is child_node_id, target is parent_node_id
      const parentNodeName = getNodeName(relationship.parent_node_id);
      return `Connection to ${parentNodeName || 'Unknown Node'}`;
    }
  }
  return field.name || field.label || 'Unnamed Column';
}

// Custom collision detection that uses pointerWithin for source field drags
// to ensure the pointer is actually over the target element
function customCollisionDetection(args: any) {
  // First check if we're dragging a source field
  const activeData = args.active.data.current;
  if (activeData?.type === 'source-field') {
    // For source field drags, use pointerWithin to ensure pointer is over the target
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
    // If pointer is not over any target, return empty array
    return [];
  }
  // For field reordering, use closestCenter
  return closestCenter(args);
}

// Component to monitor drag events and track hover state
function DragMonitor({ 
  setHoveredDropTarget, 
  workingCopy 
}: { 
  setHoveredDropTarget: (target: string | null) => void;
  workingCopy: PipelineNode | null;
}) {
  useDndMonitor({
    onDragOver: (event) => {
      // Track which drop target is being hovered over
      // Only track when dragging a source field and collision detection found a target
      if (event.active.data.current?.type === 'source-field') {
        if (event.over) {
          const overId = typeof event.over.id === 'string' ? event.over.id : String(event.over.id);
          if (overId.startsWith('drop-target-')) {
            setHoveredDropTarget(overId);
          } else {
            // Also check if we're over a field directly (fallback)
            const fieldId = overId;
            const field = workingCopy?.fields?.find(
              (f) => (f.id || f.name || '') === fieldId
            );
            if (field) {
              setHoveredDropTarget(`drop-target-${fieldId}`);
            } else {
              setHoveredDropTarget(null);
            }
          }
        } else {
          // No collision detected - clear hover state
          setHoveredDropTarget(null);
        }
      } else {
        setHoveredDropTarget(null);
      }
    },
    onDragEnd: () => {
      setHoveredDropTarget(null);
    },
  });
  
  return null;
}

// Draggable source field component
interface DraggableSourceFieldProps {
  nodeId: string;
  field: NonNullable<PipelineNode['fields']>[0];
  mappedFieldCount: number;
  onBadgeClick: () => void;
}

function DraggableSourceField({ nodeId, field, mappedFieldCount, onBadgeClick }: DraggableSourceFieldProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `source-field-${nodeId}-${field.id || field.name}`,
    data: {
      type: 'source-field',
      nodeId,
      fieldId: field.id || field.name,
      fieldName: field.name || field.label,
      fieldType: field.type,
      attributeId: field.id || field.name,
      attributeKey: field.name || field.label,
    },
  });

  return (
    <div
      ref={setNodeRef}
      style={{ 
        visibility: isDragging ? 'hidden' : 'visible',
      }}
      className="px-3 py-2 bg-muted rounded-md text-sm"
    >
      <div className="flex items-center justify-between gap-2">
        <div
          {...listeners}
          {...attributes}
          className="flex-1 min-w-0 cursor-grab active:cursor-grabbing"
        >
          <div className="font-medium">
            {field.name || field.label || '-'}
          </div>
          {field.type && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {field.type}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {mappedFieldCount > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onBadgeClick();
              }}
              className="h-5 min-w-[20px] px-1.5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-semibold hover:bg-primary/90 transition-colors"
            >
              {mappedFieldCount > 9 ? '9+' : mappedFieldCount}
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Placeholder for View Stats
            }}
            className="h-5 w-5 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            title="View Stats"
          >
            <BarChart3 className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Placeholder for View Lineage
            }}
            className="h-5 w-5 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            title="View Lineage"
          >
            <GitBranch className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper function to check if a field cannot accept source column mappings
function cannotAcceptMappings(field: NonNullable<PipelineNode['fields']>[0], isCalculated: boolean): boolean {
  return isCalculated || field.type === 'DENORMALIZED' || field.type === 'FOREIGN_KEY';
}

// Sortable field item component
interface SortableFieldItemProps {
  field: NonNullable<PipelineNode['fields']>[0];
  isCalculated: boolean;
  totalMappedColumns: number;
  hasTransformer: boolean;
  onConfigClick: () => void;
  onDeleteClick: () => void;
  isHovered?: boolean;
  isRecentlyMapped?: boolean;
  currentNodeId?: string;
  getRelationship?: (relationshipId: string) => any;
  getNodeName?: (nodeId: string) => string | undefined;
  fieldRef?: (node: HTMLDivElement | null) => void;
}

function SortableFieldItem({
  field,
  isCalculated,
  totalMappedColumns,
  hasTransformer,
  onConfigClick,
  onDeleteClick,
  isHovered = false,
  isRecentlyMapped = false,
  currentNodeId,
  getRelationship,
  getNodeName,
  fieldRef,
}: SortableFieldItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id || field.name || '' });

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `drop-target-${field.id || field.name}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Combine refs
  const combinedRef = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    setDroppableRef(node);
    if (fieldRef) {
      fieldRef(node);
    }
  };

  // Show hover state if either isOver from useDroppable or isHovered prop is true
  const showHover = isOver || isHovered;
  const cannotMap = cannotAcceptMappings(field, isCalculated);

  const isPartOfCompositeKey = field.part_of_composite_key === true;

  return (
    <Card 
      ref={combinedRef} 
      style={style} 
      className={`p-3 transition-colors duration-500 ${
        isPartOfCompositeKey
          ? 'border-accent'
          : ''
      } ${
        isRecentlyMapped 
          ? 'bg-primary/20' 
          : showHover 
            ? cannotMap
              ? 'bg-destructive/10' 
              : 'bg-muted' 
            : ''
      }`}
    >
      <div className="flex items-center gap-3">
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing flex-shrink-0"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>

        {/* Left icon - mapped, calculated, foreign key, or denormalized */}
        <div className="relative flex-shrink-0">
          {isCalculated ? (
            <Calculator className="h-5 w-5 text-primary" />
          ) : field.type === 'FOREIGN_KEY' ? (
            <Key className="h-5 w-5 text-primary" />
          ) : field.type === 'DENORMALIZED' ? (
            <Copy className="h-5 w-5 text-primary" />
          ) : (
            <>
              <Link2 className="h-5 w-5 text-muted-foreground" />
              {totalMappedColumns > 0 && (
                <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-semibold">
                  {totalMappedColumns > 9 ? '9+' : totalMappedColumns}
                </div>
              )}
            </>
          )}
        </div>

        {/* Column name and type */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">
              {getFieldName(field, getRelationship, getNodeName, currentNodeId)}
            </span>
            {hasTransformer && (
              <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
            )}
          </div>
          {field.type && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {field.type}
            </p>
          )}
        </div>

        {/* Config button */}
        <button
          onClick={onConfigClick}
          className="h-5 w-5 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          title="Settings"
        >
          <Settings className="h-4 w-4" />
        </button>
        
        {/* Delete button */}
        <button
          onClick={onDeleteClick}
          className="h-5 w-5 flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
          title="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </Card>
  );
}

export interface NodeDetailBreadcrumbConfig {
  parentLabel: string;
  parentPath: string;
}

export type SidebarNodeFilter = 'nodes' | 'entities';

// Column stats type for sidebar
interface ColumnStats {
  key: string;
  distinct_values: number;
  empty_values: number;
  uniqueness: number;
  density: number;
  samples: any[];
  enhanced?: boolean;
}

interface NodeDetailProps {
  breadcrumbConfig?: NodeDetailBreadcrumbConfig;
  sidebarNodeFilter?: SidebarNodeFilter;
}

export default function NodeDetail({ breadcrumbConfig, sidebarNodeFilter = 'nodes' }: NodeDetailProps = {}) {
  const parentLabel = breadcrumbConfig?.parentLabel ?? 'Modeling';
  const parentPath = breadcrumbConfig?.parentPath ?? '/develop/modeling';
  const { id } = useParams<{ id: string }>();
  const { data: node, isLoading, error } = usePipelineNode(id || null);
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [workingCopy, setWorkingCopy] = useState<PipelineNode | null>(null);
  const [saving, setSaving] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [fieldSearchQueries, setFieldSearchQueries] = useState<Record<string, string>>({});
  const [fieldPageNumbers, setFieldPageNumbers] = useState<Record<string, number>>({});
  const [fieldConfigDialogOpen, setFieldConfigDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<NonNullable<PipelineNode['fields']>[0] | null>(null);
  // Track the original field name when editing starts - needed for DENORMALIZED field lookup
  // because the relationship's denormalized_fields uses the original name until save
  const [originalEditingFieldName, setOriginalEditingFieldName] = useState<string | null>(null);
  const [deleteFieldDialogOpen, setDeleteFieldDialogOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<NonNullable<PipelineNode['fields']>[0] | null>(null);
  const [initialFieldFormData, setInitialFieldFormData] = useState<FieldFormData>({ name: '', description: '', type: '', map_options: [], part_of_composite_key: false, merge_behavior: 'PICK_ONE' });
  const [initialRelationshipData, setInitialRelationshipData] = useState<Partial<PipelineNodeRelationship> | null>(null);
  const [relationshipsToSave, setRelationshipsToSave] = useState<Map<string, Partial<PipelineNodeRelationship>>>(new Map());
  const [relationshipsToDelete, setRelationshipsToDelete] = useState<Set<string>>(new Set());
  // Pending relationships for new FOREIGN_KEY fields that don't have a relationship_id yet
  // Keyed by field name/id
  const [pendingRelationships, setPendingRelationships] = useState<Map<string, Partial<PipelineNodeRelationship>>>(new Map());
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [activeDragData, setActiveDragData] = useState<any>(null);
  const [hoveredDropTarget, setHoveredDropTarget] = useState<string | null>(null);
  const [recentlyMappedFieldId, setRecentlyMappedFieldId] = useState<string | null>(null);
  const [flashingFieldIds, setFlashingFieldIds] = useState<Set<string>>(new Set());
  const [rebuildModalOpen, setRebuildModalOpen] = useState(false);
  const [unsavedChangesWarningOpen, setUnsavedChangesWarningOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('basic-info');
  // Stats and lineage dialog state for sidebar
  const [sidebarStatsDialogOpen, setSidebarStatsDialogOpen] = useState(false);
  const [sidebarStatsNodeId, setSidebarStatsNodeId] = useState<string>('');
  const [sidebarStatsField, setSidebarStatsField] = useState<PipelineNodeField | null>(null);
  const [sidebarColumnStats, setSidebarColumnStats] = useState<ColumnStats | null>(null);
  const [sidebarLoadingStats, setSidebarLoadingStats] = useState(false);
  const [sidebarLineageDialogOpen, setSidebarLineageDialogOpen] = useState(false);
  const [sidebarLineageNodeId, setSidebarLineageNodeId] = useState<string>('');
  const [sidebarLineageField, setSidebarLineageField] = useState<PipelineNodeField | null>(null);
  const [sidebarColumnLineage, setSidebarColumnLineage] = useState<ColumnLineageGraphType | null>(null);
  const [sidebarLoadingLineage, setSidebarLoadingLineage] = useState(false);
  // Delete and Copy model dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyName, setCopyName] = useState('');
  const [isCopying, setIsCopying] = useState(false);
  const [buildErrorDialogOpen, setBuildErrorDialogOpen] = useState(false);
  const navigate = useNavigate();
  const isInDraftMode = useIsInDraftMode();
  const templateName = useTemplateName(node?.managed_by_template_id);
  const rightColumnScrollRef = useRef<HTMLDivElement>(null);
  const fieldRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const editorRef = useRef<any>(null);

  // Function to insert text at cursor position in Monaco editor
  const insertAtCursor = useCallback((text: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    const selection = editor.getSelection();
    const id = { major: 1, minor: 1 };
    const op = {
      identifier: id,
      range: selection,
      text: text,
      forceMoveMarkers: true,
    };
    editor.executeEdits('insert-ref', [op]);
    editor.focus();
  }, []);

  const handleSidebarViewStats = useCallback(async (nodeId: string, field: PipelineNodeField) => {
    setSidebarStatsNodeId(nodeId);
    setSidebarStatsField(field);
    setSidebarStatsDialogOpen(true);
    setSidebarLoadingStats(true);
    setSidebarColumnStats(null);

    const fieldId = field.id;
    const fieldName = field.name;
    const fieldKey = fieldId || fieldName;

    if (!fieldKey) {
      toastService.error('Field ID or name is required');
      setSidebarLoadingStats(false);
      return;
    }

    try {
      const result = await ApiService.getInstance().request(
        'GET',
        `/pipelinenodes/${nodeId}/column/${fieldKey}/shape`
      ) as { record: ColumnStats };

      setSidebarColumnStats(result.record);
    } catch (err) {
      toastService.error('Failed to load column statistics');
      console.error('Error loading column stats:', err);
    } finally {
      setSidebarLoadingStats(false);
    }
  }, []);

  // Handler for viewing column lineage from sidebar
  const handleSidebarViewLineage = useCallback(async (nodeId: string, field: PipelineNodeField) => {
    setSidebarLineageNodeId(nodeId);
    setSidebarLineageField(field);
    setSidebarLineageDialogOpen(true);
    setSidebarLoadingLineage(true);
    setSidebarColumnLineage(null);

    const fieldId = field.id;

    if (!fieldId) {
      toastService.error('Field ID is required');
      setSidebarLoadingLineage(false);
      return;
    }

    try {
      const result = await ApiService.getInstance().request(
        'GET',
        `/pipelinenodes/${nodeId}/column-lineage`,
        { field_id: fieldId }
      ) as ColumnLineageGraphType;

      setSidebarColumnLineage(result);
    } catch (err) {
      toastService.error('Failed to load column lineage');
      console.error('Error loading column lineage:', err);
    } finally {
      setSidebarLoadingLineage(false);
    }
  }, []);

  // Set up drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch all pipeline nodes for the sidebar
  const { data: allNodesData, isLoading: isLoadingNodes } = usePipelineNodes({
    page: 1,
    pageSize: 1000, // Large page size to get all nodes
  });

  // Fetch all relationships for lookup
  const { data: relationshipsData } = usePipelineNodeRelationships();

  // Column distribution query for sidebar stats dialog
  const sidebarColumnDistributionQuery = useColumnValueDistribution(
    sidebarStatsNodeId,
    sidebarStatsField?.id,
    20,
    sidebarStatsDialogOpen && !!sidebarStatsNodeId && !!sidebarStatsField?.id
  );

  // Filter nodes based on sidebarNodeFilter prop
  const availableNodes = useMemo(() => {
    const allNodes = allNodesData?.records || [];
    if (sidebarNodeFilter === 'entities') {
      // For entities, show DIMENSION, DATE_DIMENSION, SOURCE, SOURCE_FILE, SEED_SOURCE, STAGING, and CUSTOM
      const includedTypes = ['DIMENSION', 'DATE_DIMENSION', 'SOURCE', 'SOURCE_FILE', 'SEED_SOURCE', 'STAGING', 'CUSTOM'];
      return allNodes.filter((n: PipelineNode) => includedTypes.includes(n.node_type));
    } else {
      // For nodes, exclude DIMENSION, DATE_DIMENSION, REPORT, DESTINATION
      const excludedTypes = ['DIMENSION', 'DATE_DIMENSION', 'REPORT', 'DESTINATION'];
      return allNodes.filter((n: PipelineNode) => !excludedTypes.includes(n.node_type));
    }
  }, [allNodesData?.records, sidebarNodeFilter]);

  // Get selected node details
  const selectedNode = availableNodes.find((n: PipelineNode) => n.id === selectedNodeId);

  // Get upstream nodes
  const upstreamNodes = useMemo(() => {
    // Use workingCopy if available, otherwise fall back to node
    const upstreamNodeIds = workingCopy?.upstream_node_ids || node?.upstream_node_ids;
    if (!upstreamNodeIds || !allNodesData?.records) {
      return [];
    }
    return upstreamNodeIds
      .map((id: string) => allNodesData.records.find((n: PipelineNode) => n.id === id))
      .filter((n): n is PipelineNode => n !== undefined);
  }, [workingCopy?.upstream_node_ids, node?.upstream_node_ids, allNodesData?.records]);

  // Helper function to check if an element is visible in the viewport and scroll it into view if needed
  const scrollFieldIntoView = useCallback((fieldId: string) => {
    const fieldElement = fieldRefsMap.current.get(fieldId);
    const scrollContainer = rightColumnScrollRef.current;
    
    if (!fieldElement || !scrollContainer) return;
    
    // Check if element is visible in the scroll container
    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = fieldElement.getBoundingClientRect();
    
    // Check if element is at least partially visible within the container
    // Element is visible if any part of it is within the container bounds
    const isVisible = 
      elementRect.bottom > containerRect.top &&
      elementRect.top < containerRect.bottom;
    
    if (!isVisible) {
      // Scroll the element into view with smooth behavior
      fieldElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }
  }, []);

  // Create a map of source_column_id -> all field IDs it's been mapped to
  const sourceColumnToFieldIdsMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    if (!workingCopy?.fields) return map;

    const addToMap = (sourceNodeId: string, attributeId: string | undefined, attributeKey: string | undefined, fieldId: string) => {
      if (!sourceNodeId) return;
      
      // Add entry for attribute_id if present
      if (attributeId) {
        const sourceColumnId = `${sourceNodeId}:${attributeId}`;
        if (!map.has(sourceColumnId)) {
          map.set(sourceColumnId, new Set());
        }
        map.get(sourceColumnId)!.add(fieldId);
      }
      
      // Add entry for attribute_key if present and different from attribute_id
      if (attributeKey && attributeKey !== attributeId) {
        const sourceColumnId = `${sourceNodeId}:${attributeKey}`;
        if (!map.has(sourceColumnId)) {
          map.set(sourceColumnId, new Set());
        }
        map.get(sourceColumnId)!.add(fieldId);
      }
    };

    workingCopy.fields.forEach((field) => {
      const fieldId = field.id || field.name || '';
      if (!field.map_options || field.map_options.length === 0) return;

      field.map_options.forEach((option) => {
        // Handle sub_options if they exist
        if (option.sub_options && option.sub_options.length > 0) {
          option.sub_options.forEach((sub) => {
            if (sub.source_node_id) {
              addToMap(sub.source_node_id, sub.attribute_id, sub.attribute_key, fieldId);
            }
          });
        } else {
          // Handle regular map options
          if (option.source_node_id) {
            addToMap(option.source_node_id, option.attribute_id, option.attribute_key, fieldId);
          }
        }
      });
    });

    return map;
  }, [workingCopy?.fields]);

  // Create lookup functions for relationships and nodes
  const getRelationship = useMemo(() => {
    if (!relationshipsData) return () => undefined;
    const relationshipsById = new Map(relationshipsData.map(rel => [rel.id, rel]));
    return (relationshipId: string) => relationshipsById.get(relationshipId);
  }, [relationshipsData]);

  const getNodeName = useMemo(() => {
    if (!allNodesData?.records) return () => undefined;
    const nodesById = new Map(allNodesData.records.map(n => [n.id, n]));
    return (nodeId: string) => {
      const node = nodesById.get(nodeId);
      return node?.label || node?.name;
    };
  }, [allNodesData?.records]);

  // Memoize combobox options for mapped fields dialog
  const mappedFieldComboboxOptions = useMemo(() => {
    const options: ComboboxOption[] = [];
    upstreamNodes.forEach((upstreamNode) => {
      (upstreamNode.fields || []).forEach((field) => {
        options.push({
          value: `${upstreamNode.id}:${field.id || field.name}`,
          label: `${field.name || field.label || 'Unnamed'}`,
          description: `${upstreamNode.label || upstreamNode.name || 'Unnamed Node'} • ${field.type || 'Unknown'}`,
        });
      });
    });
    return options;
  }, [upstreamNodes]);

  // Memoize parent node options for relationship configuration
  // Only DIMENSION and DATE_DIMENSION nodes can be parents in foreign key relationships
  const parentNodeOptions = useMemo(() => {
    if (!allNodesData?.records) return [];
    return allNodesData.records
      .filter((n) => n.id !== id && (n.node_type === 'DIMENSION' || n.node_type === 'DATE_DIMENSION'))
      .map((n) => ({
        value: n.id,
        label: n.label || n.name || 'Unnamed Node',
        description: n.node_type,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allNodesData?.records, id]);

  // Memoize foreign key relationship options for denormalized fields
  // This includes both saved relationships AND pending (unsaved) relationships
  const foreignKeyRelationshipOptions = useMemo(() => {
    const options: { value: string; label: string; description: string }[] = [];

    // Add saved relationships where current node is the child
    if (relationshipsData && id) {
      relationshipsData
        .filter((rel) => rel.child_node_id === id)
        .forEach((rel) => {
          const parentNode = allNodesData?.records?.find((n) => n.id === rel.parent_node_id);
          options.push({
            value: rel.id,
            label: rel.name || `Connection to ${parentNode?.label || parentNode?.name || 'Unknown Node'}`,
            description: parentNode?.label || parentNode?.name || 'Unknown Node',
          });
        });
    }

    // Add pending relationships (unsaved FOREIGN_KEY columns)
    pendingRelationships.forEach((pendingRel, fieldKey) => {
      if (pendingRel.parent_node_id) {
        const parentNode = allNodesData?.records?.find((n) => n.id === pendingRel.parent_node_id);
        // Use "pending:" prefix so we can identify pending relationships
        options.push({
          value: `pending:${fieldKey}`,
          label: pendingRel.name || `Connection to ${parentNode?.label || parentNode?.name || 'Unknown Node'} (unsaved)`,
          description: `${parentNode?.label || parentNode?.name || 'Unknown Node'} (not yet saved)`,
        });
      }
    });

    return options;
  }, [relationshipsData, id, allNodesData?.records, pendingRelationships]);

  // Check if there are unsaved changes (dirty state)
  const isDirty = useMemo(() => {
    if (!node || !workingCopy) return false;

    // Check for pending relationship changes
    if (relationshipsToSave.size > 0 || relationshipsToDelete.size > 0 || pendingRelationships.size > 0) {
      return true;
    }

    // Compare relevant fields between workingCopy and original node
    // We use JSON.stringify for deep comparison of objects/arrays
    const fieldsToCompare: (keyof PipelineNode)[] = [
      'label',
      'description',
      'table_name',
      'composite_key',
      'last_modified_column',
      'fields',
      'upstream_node_ids',
      'grouping_behavior',
      'upstream_pre_filters',
      'data_whitelist',
      'special_node_args',
      'custom_sql',
      'incremental_build',
      'incremental_timestamp_field_id',
      'dedupe_rules',
      'include_in_snapshots',
    ];

    for (const field of fieldsToCompare) {
      const originalValue = node[field];
      const currentValue = workingCopy[field];

      // Handle undefined/null equivalence
      const originalIsEmpty = originalValue === undefined || originalValue === null || originalValue === '';
      const currentIsEmpty = currentValue === undefined || currentValue === null || currentValue === '';

      if (originalIsEmpty && currentIsEmpty) {
        continue;
      }

      // For arrays and objects, use JSON.stringify for comparison
      if (typeof originalValue === 'object' || typeof currentValue === 'object') {
        if (JSON.stringify(originalValue) !== JSON.stringify(currentValue)) {
          return true;
        }
      } else if (originalValue !== currentValue) {
        return true;
      }
    }

    return false;
  }, [node, workingCopy, relationshipsToSave.size, relationshipsToDelete.size, pendingRelationships.size]);

  // Helper function to get record count
  const getRecordCount = (node: PipelineNode): number | undefined => {
    return node.shape?.total_records ?? node.total_records;
  };

  // Helper function to format number
  const formatNumber = (num?: number): string => {
    if (num === undefined || num === null) return '-';
    return new Intl.NumberFormat('en-US').format(num);
  };

  // Toggle node expansion
  const toggleNodeExpansion = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  // Get filtered and sorted fields for a node
  const getFilteredFields = (node: PipelineNode) => {
    const fields = node.fields || [];
    const searchQuery = fieldSearchQueries[node.id]?.toLowerCase() || '';
    
    let filtered = fields;
    if (searchQuery) {
      filtered = fields.filter((field) => {
        const name = (field.name || '').toLowerCase();
        const label = (field.label || '').toLowerCase();
        return name.includes(searchQuery) || label.includes(searchQuery);
      });
    }
    
    // Sort alphabetically by name or label
    const sorted = [...filtered].sort((a, b) => {
      const aName = (a.name || a.label || '').toLowerCase();
      const bName = (b.name || b.label || '').toLowerCase();
      return aName.localeCompare(bName);
    });
    
    // Return all sorted fields and total matching count
    return {
      fields: sorted,
      totalMatching: filtered.length,
    };
  };

  // Check if node is CUSTOM type (before hooks, using node from query)
  const isCustomNode = node?.node_type === 'CUSTOM';
  const isSourceNode = node?.node_type === 'SOURCE';
  const isDateDimension = node?.node_type === 'DATE_DIMENSION';
  const isEntityNode = node?.node_type === 'DIMENSION' || node?.node_type === 'DATE_DIMENSION';
  const isFlatFile = node?.flat_file === true;

  // Template-locked: node is managed by a template, not unlocked, and not a DIMENSION/DATE_DIMENSION
  const isTemplateLocked = !!node?.managed_by_template_id && !node?.unlocked && !isEntityNode;

  // Entity (DIMENSION/DATE_DIMENSION) template-locked: granular locking (name, description, grouping, managed fields)
  const isEntityTemplateLocked = !!node?.managed_by_template_id && !node?.unlocked && isEntityNode;

  // Hooks for SOURCE node configuration - MUST be called before any conditional returns
  const sourcesQuery = useSources({ enabled: isSourceNode });
  const availableTables = useAvailableSnowflakeTables();
  // Only fetch source columns if we have a node ID (node is saved) and it's a SOURCE node with a table_name
  // AND it's NOT a flat_file (flat_file nodes get columns from node.fields instead)
  // Use node.table_name (saved) rather than workingCopy.table_name (unsaved changes)
  const sourceColumns = useSourceColumns(
    isSourceNode && !isFlatFile && node?.id && node?.table_name ? node.id : null
  );

  // Compute initial SQL for the Monaco editor (uncontrolled via defaultValue).
  // Uses node ID as the key to reset the editor when navigating between nodes.
  const initialSql = useMemo(() => {
    if (!node) return '';
    return (!node.custom_sql && node.cached_generated_sql) ? node.cached_generated_sql : (node.custom_sql || '');
  }, [node?.id, node?.custom_sql, node?.cached_generated_sql]);

  // Initialize working copy when node loads
  useEffect(() => {
    if (node) {
      setWorkingCopy({ ...node });
    }
  }, [node]);


  const handleSave = async () => {
    if (!workingCopy || !id) return;

    setSaving(true);
    try {
      const tableNameChanged = node?.table_name !== workingCopy.table_name;

      // IMPORTANT: Save order matters!
      // 1. Save node FIRST (so FK/DENORMALIZED fields exist in DB with relationship_id = undefined)
      // 2. Then save relationships (backend will find and link existing fields by name)
      // 3. Don't save node again (backend already updated it when saving relationships)

      // Prepare fields for save: clear pending relationship IDs so backend can find and link them
      // Fields with 'pending:...' relationship_id should be saved with undefined so they can be linked
      const fieldsForSave = workingCopy.fields?.map((f) => {
        if (isPendingRelationshipId((f as PipelineNodeField).relationship_id)) {
          return {
            ...f,
            relationship_id: undefined,
          };
        }
        return f;
      });

      // Save the node first (with relationship_id = undefined for new FK/DENORMALIZED fields)
      await pipelineNodeService.save({ ...workingCopy, fields: fieldsForSave });

      // Now save all pending relationships (backend will link existing fields by name)
      for (const [fieldKey, pendingRel] of pendingRelationships.entries()) {
        if (pendingRel.parent_node_id) {
          // Find the FK field to get its name for child_foreign_key_name
          const fkField = workingCopy.fields?.find((f) =>
            (f.id === fieldKey || f.name === fieldKey) && f.type === 'FOREIGN_KEY'
          );

          const relationshipToSave = preparePendingRelationshipForSave(
            pendingRel,
            id,
            pendingRel.child_foreign_key_name || fkField?.name || ''
          );

          await pipelineNodeRelationshipService.save(relationshipToSave);
        }
      }

      // Prepare relationship save/delete operations using utility function
      // This ensures relationships marked for deletion are not also saved
      const relOps = prepareRelationshipSaveOperations(relationshipsToSave, relationshipsToDelete);

      // Save all queued relationships (for DENORMALIZED fields and relationship changes)
      if (relOps.relationshipsToSave.length > 0) {
        const savePromises = relOps.relationshipsToSave.map((relData) =>
          pipelineNodeRelationshipService.save(relData)
        );
        await Promise.all(savePromises);
      }

      // Delete all flagged relationships
      if (relOps.relationshipIdsToDelete.length > 0) {
        const deletePromises = relOps.relationshipIdsToDelete.map((relId) =>
          pipelineNodeRelationshipService.delete(relId)
        );
        await Promise.all(deletePromises);
      }
      
      // Invalidate queries to refetch - nodes, relationships, metrics, and dimensions
      // may all be affected by node changes
      queryClient.invalidateQueries({ queryKey: ['pipeline-node', id] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes-build-tree'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-node-relationships'] });
      queryClient.invalidateQueries({ queryKey: ['measures'] });
      queryClient.invalidateQueries({ queryKey: ['measure'] });
      queryClient.invalidateQueries({ queryKey: ['reporting-tree'] });
      // If table_name changed and it's a SOURCE node, refetch source columns
      if (tableNameChanged && isSourceNode) {
        queryClient.invalidateQueries({ queryKey: ['pipeline_node_source_columns', id] });
      }
      
      // Clear queued relationships after successful save
      setRelationshipsToSave(new Map());
      setRelationshipsToDelete(new Set());
      setPendingRelationships(new Map());

      toastService.success('Node saved successfully');
    } catch (err) {
      console.error('Failed to save node:', err);
      toastService.error('Failed to save node');
    } finally {
      setSaving(false);
    }
  };

  useUnsavedChangesGuard(isDirty, handleSave);

  // Handle rebuild button click - check for unsaved changes first
  const handleRebuildClick = () => {
    if (isDirty) {
      setUnsavedChangesWarningOpen(true);
    } else {
      setRebuildModalOpen(true);
    }
  };

  // Handle save then rebuild - save changes first, then open rebuild modal
  const handleSaveThenRebuild = async () => {
    setUnsavedChangesWarningOpen(false);
    await handleSave();
    // Only open rebuild modal if save was successful (saving will be false and isDirty will be false)
    // We need to wait for state to settle, so we'll open it after a brief delay
    // Since handleSave sets saving=false in finally, we can open the modal right after
    setRebuildModalOpen(true);
  };

  // Handle delete model
  const handleDeleteModel = async () => {
    if (!node || deleteConfirmName !== (node.label || node.name)) return;

    setIsDeleting(true);
    try {
      await pipelineNodeService.delete(node.id);
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes-build-tree'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-node-relationships'] });
      queryClient.invalidateQueries({ queryKey: ['measures'] });
      queryClient.invalidateQueries({ queryKey: ['reporting-tree'] });
      toastService.success('Model deleted successfully');
      setDeleteDialogOpen(false);
      // Navigate back to the models list
      navigate('/develop/modeling');
    } catch (err) {
      console.error('Failed to delete model:', err);
      toastService.error('Failed to delete model');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle copy model
  const handleCopyModel = async () => {
    if (!node || !copyName.trim()) return;

    setIsCopying(true);
    try {
      // Use the dedicated copy endpoint which also copies relationships and visualizations
      const copiedNode = await pipelineNodeService.copy(node.id, copyName.trim());
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes-build-tree'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-node-relationships'] });
      toastService.success('Model copied successfully');
      setCopyDialogOpen(false);
      setCopyName('');
      // Navigate to the new model
      navigate(`/develop/modeling/${copiedNode.id}`);
    } catch (err) {
      console.error('Failed to copy model:', err);
      toastService.error('Failed to copy model');
    } finally {
      setIsCopying(false);
    }
  };


  // Define custom Monaco theme to match app dark mode background
  function handleEditorWillMount(monaco: Monaco) {
    monaco.editor.defineTheme('app-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#020817',
      },
    });
  }

  // Table options for combobox
  // Format: DATABASE.SCHEMA.TABLE (matching the format stored in node.table_name)
  const sourceOptions: ComboboxOption[] = useMemo(() => {
    return (sourcesQuery.data?.records || []).map((s) => ({
      value: s.id,
      label: s.name,
    }));
  }, [sourcesQuery.data?.records]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading node...</p>
        </div>
      </div>
    );
  }

  if (error || !node) {
    return (
      <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md m-8">
        <p className="text-destructive text-sm">
          Failed to load node. Please try again.
        </p>
      </div>
    );
  }

  const tableOptions: ComboboxOption[] = availableTables.data?.map((table) => ({
    value: `${table.database_name}.${table.schema_name}.${table.name}`,
    label: table.name,
    description: `Schema: ${table.database_name}.${table.schema_name}`,
  })) || [];

  // Column options for composite key and last modified
  // For flat_file nodes, use node.fields; otherwise use sourceColumns from snowflake table
  const columnOptions: MultiSelectComboboxOption[] = isFlatFile && workingCopy?.fields
    ? workingCopy.fields.map((field) => ({
        value: field.name || '',
        label: field.name || field.label || '',
        description: field.type || '',
      }))
    : sourceColumns.data?.map((col) => ({
        value: col.name,
        label: col.name,
        description: col.type,
      })) || [];

  const lastModifiedColumnOptions: ComboboxOption[] = isFlatFile && workingCopy?.fields
    ? workingCopy.fields.map((field) => ({
        value: field.name || '',
        label: field.name || field.label || '',
        description: field.type || '',
      }))
    : sourceColumns.data?.map((col) => ({
        value: col.name,
        label: col.name,
        description: col.type,
      })) || [];

  // Render configuration content with tabs
  const renderConfigurationContent = () => {
    if (isDateDimension) {
      return (
        <Tabs defaultValue="calendar-config" className="h-full flex flex-col">
          <TabsList className="mx-4 mt-4">
            <TabsTrigger value="calendar-config">Calendar Configuration</TabsTrigger>
            <TabsTrigger value="reporting-periods">Reporting Periods</TabsTrigger>
            <TabsTrigger value="data">View Data</TabsTrigger>
          </TabsList>
          <TabsContent value="calendar-config" className="flex-1 overflow-auto px-4 py-4 mt-4">
            <div className="space-y-6 max-w-md">
              <div>
                <h3 className="text-lg font-medium mb-4">Date Range</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Configure the date range for this calendar dimension. Leave empty to use defaults (1/1/2020 - TODAY).
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start-date">Start Date</Label>
                    <div className="flex gap-2">
                      <Input
                        id="start-date"
                        type="date"
                        value={workingCopy?.special_node_args?.start_date || ''}
                        onChange={(e) => {
                          if (workingCopy) {
                            setWorkingCopy({
                              ...workingCopy,
                              special_node_args: {
                                ...workingCopy.special_node_args,
                                start_date: e.target.value || undefined,
                              },
                            });
                          }
                        }}
                      />
                      {workingCopy?.special_node_args?.start_date && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (workingCopy) {
                              setWorkingCopy({
                                ...workingCopy,
                                special_node_args: {
                                  ...workingCopy.special_node_args,
                                  start_date: undefined,
                                },
                              });
                            }
                          }}
                          title="Clear start date"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-date">End Date</Label>
                    <div className="flex gap-2">
                      <Input
                        id="end-date"
                        type="date"
                        value={workingCopy?.special_node_args?.end_date || ''}
                        onChange={(e) => {
                          if (workingCopy) {
                            setWorkingCopy({
                              ...workingCopy,
                              special_node_args: {
                                ...workingCopy.special_node_args,
                                end_date: e.target.value || undefined,
                              },
                            });
                          }
                        }}
                      />
                      {workingCopy?.special_node_args?.end_date && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (workingCopy) {
                              setWorkingCopy({
                                ...workingCopy,
                                special_node_args: {
                                  ...workingCopy.special_node_args,
                                  end_date: undefined,
                                },
                              });
                            }
                          }}
                          title="Clear end date"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-4">Week Settings</h3>
                <div className="space-y-2">
                  <Label htmlFor="week-start-day">Week Start Day</Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Choose which day of the week is considered the first day.
                  </p>
                  <Select
                    value={String(workingCopy?.special_node_args?.week_start_day_index ?? '0')}
                    onValueChange={(value) => {
                      if (workingCopy) {
                        setWorkingCopy({
                          ...workingCopy,
                          special_node_args: {
                            ...workingCopy.special_node_args,
                            week_start_day_index: Number(value),
                          },
                        });
                      }
                    }}
                  >
                    <SelectTrigger id="week-start-day" className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Sunday</SelectItem>
                      <SelectItem value="1">Monday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="reporting-periods" className="flex-1 overflow-auto px-4 py-4 mt-4">
            <ReportingPeriodsTab />
          </TabsContent>
          <TabsContent value="data" className="flex-1 overflow-hidden px-4 py-4 mt-4">
            {id && (
              <NodeDataTable
                pipelineNodeId={id}
                search=""
                defaultSort=""
              />
            )}
          </TabsContent>
        </Tabs>
      );
    } else if (isCustomNode) {
      return (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
          <TabsList className="mx-4 mt-4">
            <TabsTrigger value="basic-info">General</TabsTrigger>
            <TabsTrigger value="sql">SQL</TabsTrigger>
            <TabsTrigger value="connections">Connections</TabsTrigger>
            <TabsTrigger value="data">View Data</TabsTrigger>
          </TabsList>
          <TabsContent value="basic-info" className="flex-1 overflow-auto px-4 py-4 mt-4">
            <BasicInfoTab
              workingCopy={workingCopy}
              isSourceNode={false}
              isFlatFile={false}
              tableOptions={[]}
              columnOptions={[]}
              lastModifiedColumnOptions={[]}
              availableTablesLoading={false}
              sourceColumnsLoading={false}
              node={node}
              onChange={(updates) => {
                if (workingCopy) {
                  setWorkingCopy({ ...workingCopy, ...updates });
                }
              }}
            />
          </TabsContent>
          <TabsContent value="sql" className="flex-1 overflow-hidden mt-4 p-0">
            <Editor
              key={`${node?.id}-${initialSql}`}
              height="100%"
              defaultLanguage="sql"
              defaultValue={initialSql}
              onChange={(value) => setWorkingCopy((prev) => prev ? { ...prev, custom_sql: value || '' } : prev)}
              beforeMount={handleEditorWillMount}
              onMount={(editor) => { editorRef.current = editor; }}
              theme={theme === 'dark' ? 'app-dark' : 'vs'}
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                wordWrap: 'on',
                automaticLayout: true,
              }}
            />
          </TabsContent>
          <TabsContent value="connections" className="flex-1 overflow-hidden px-4 py-4 mt-4">
            {id && <ConnectionsTab nodeId={id} />}
          </TabsContent>
          <TabsContent value="data" className="flex-1 overflow-hidden px-4 py-4 mt-4">
            {id && (
              <NodeDataTable
                pipelineNodeId={id}
                search=""
                defaultSort=""
              />
            )}
          </TabsContent>
        </Tabs>
      );
    } else {
      return (
        <Tabs defaultValue="basic-info" className="h-full flex flex-col">
          <TabsList className="mx-4 mt-4">
            <TabsTrigger value="basic-info">General</TabsTrigger>
            {!isSourceNode && (
              <TabsTrigger value="sources-columns">Sources & Columns</TabsTrigger>
            )}
            <TabsTrigger value="filtering">Filtering</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
            {isEntityNode && (
              <TabsTrigger value="relationships">Relationships</TabsTrigger>
            )}
            <TabsTrigger value="connections">Connections</TabsTrigger>
            <TabsTrigger value="data">View Data</TabsTrigger>
          </TabsList>
          <TabsContent value="basic-info" className="flex-1 overflow-auto px-4 py-4 mt-4">
            <LockedNodeOverlay managedByTemplateId={isTemplateLocked ? node.managed_by_template_id : undefined} unlocked={false}>
              <BasicInfoTab
                workingCopy={workingCopy}
                isSourceNode={isSourceNode}
                isFlatFile={isFlatFile}
                tableOptions={tableOptions}
                columnOptions={columnOptions}
                lastModifiedColumnOptions={lastModifiedColumnOptions}
                availableTablesLoading={availableTables.isLoading || availableTables.isFetching}
                sourceColumnsLoading={sourceColumns.isLoading || sourceColumns.isFetching}
                node={node}
                onChange={(updates) => {
                  if (workingCopy) {
                    setWorkingCopy({ ...workingCopy, ...updates });
                  }
                }}
                onFieldsChange={isSourceNode ? (fields) => {
                  if (workingCopy) {
                    setWorkingCopy({ ...workingCopy, fields });
                  }
                } : undefined}
                sourceOptions={sourceOptions}
                sourcesLoading={sourcesQuery.isLoading}
                templateLocked={isEntityTemplateLocked}
              />
            </LockedNodeOverlay>
          </TabsContent>
          {!isSourceNode && (
            <TabsContent value="sources-columns" className="flex-1 overflow-hidden mt-0">
              <LockedNodeOverlay managedByTemplateId={isTemplateLocked ? node.managed_by_template_id : undefined} unlocked={false}>
              <SourcesColumnsTab
                workingCopy={workingCopy}
                upstreamNodes={upstreamNodes}
                templateLocked={isEntityTemplateLocked}
                currentNodeId={id || undefined}
                allNodes={allNodesData?.records || []}
                getRelationship={getRelationship}
                getNodeName={getNodeName}
                pendingRelationships={pendingRelationships}
                onFieldsChange={(fields) => {
                  if (workingCopy) {
                    setWorkingCopy({ ...workingCopy, fields });
                  }
                }}
                onFieldConfigOpen={(field, relationshipData) => {
                  setEditingField(field);
                  // Track the original field name for DENORMALIZED field lookup
                  setOriginalEditingFieldName(field.name || field.label || '');
                  const fieldAsPipelineNodeField = field as PipelineNodeField;
                  setInitialFieldFormData({
                    name: field.name || field.label || '',
                    description: field.description || '',
                    type: field.type || 'STRING',
                    map_options: field.map_options || [],
                    transformer: fieldAsPipelineNodeField.transformer || undefined,
                    custom_transform_sql: fieldAsPipelineNodeField.custom_transform_sql || undefined,
                    custom_sql: fieldAsPipelineNodeField.custom_sql || undefined,
                    translations: (fieldAsPipelineNodeField as any).translations || [],
                    part_of_composite_key: fieldAsPipelineNodeField.part_of_composite_key || false,
                    merge_behavior: (fieldAsPipelineNodeField as any).merge_behavior || 'PICK_ONE',
                  });
                  setInitialRelationshipData(relationshipData || null);
                  setFieldConfigDialogOpen(true);
                }}
                onFieldDelete={(field) => {
                  setFieldToDelete(field);
                  setDeleteFieldDialogOpen(true);
                }}
                onAddField={(newField) => {
                  if (workingCopy) {
                    const updatedFields = [newField, ...(workingCopy.fields || [])];
                    setWorkingCopy({ ...workingCopy, fields: updatedFields });
                  }
                }}
                onUpstreamNodeAdd={(nodeId) => {
                  if (workingCopy) {
                    const currentUpstreamIds = workingCopy.upstream_node_ids || [];
                    if (!currentUpstreamIds.includes(nodeId)) {
                      setWorkingCopy({
                        ...workingCopy,
                        upstream_node_ids: [...currentUpstreamIds, nodeId],
                      });
                    }
                  }
                }}
                onUpstreamNodeRemove={(nodeId) => {
                  if (workingCopy) {
                    // Remove from upstream_node_ids
                    const updatedUpstreamIds = (workingCopy.upstream_node_ids || []).filter(id => id !== nodeId);
                    // Remove map_options referencing that nodeId from all fields
                    const updatedFields = (workingCopy.fields || []).map(field => {
                      if (!field.map_options || field.map_options.length === 0) return field;
                      const newMapOptions = field.map_options
                        .filter(mo => mo.source_node_id !== nodeId)
                        .map(mo => {
                          if (mo.sub_options && mo.sub_options.length > 0) {
                            const newSubOptions = mo.sub_options.filter(so => so.source_node_id !== nodeId);
                            if (newSubOptions.length === 0) return null;
                            return { ...mo, sub_options: newSubOptions };
                          }
                          return mo;
                        })
                        .filter(Boolean) as typeof field.map_options;
                      return { ...field, map_options: newMapOptions };
                    });
                    // Remove upstream_pre_filters for that nodeId
                    const updatedPreFilters = { ...(workingCopy.upstream_pre_filters || {}) };
                    delete updatedPreFilters[nodeId];
                    setWorkingCopy({
                      ...workingCopy,
                      upstream_node_ids: updatedUpstreamIds,
                      fields: updatedFields,
                      upstream_pre_filters: updatedPreFilters,
                    });
                  }
                }}
                onGroupingBehaviorChange={(behavior) => {
                  if (workingCopy) {
                    // If changing to STACK, set all fields' part_of_composite_key to false
                    const updatedFields = behavior === 'STACK'
                      ? (workingCopy.fields || []).map(field => ({
                          ...field,
                          part_of_composite_key: false,
                        }))
                      : workingCopy.fields;

                    // If changing to DEDUPLICATE, initialize default dedupe rules
                    const updatedDedupeRules = behavior === 'DEDUPLICATE' && (!workingCopy.dedupe_rules || workingCopy.dedupe_rules.length === 0)
                      ? [{ field_id: '_PLB_LOADED_AT', desc: true }]
                      : workingCopy.dedupe_rules;

                    setWorkingCopy({
                      ...workingCopy,
                      grouping_behavior: behavior,
                      fields: updatedFields,
                      dedupe_rules: updatedDedupeRules,
                    });
                  }
                }}
                onUpstreamPreFiltersChange={(upstreamPreFilters) => {
                  if (workingCopy) {
                    setWorkingCopy({
                      ...workingCopy,
                      upstream_pre_filters: upstreamPreFilters,
                    });
                  }
                }}
                onDedupeRulesChange={(rules) => {
                  if (workingCopy) {
                    setWorkingCopy({ ...workingCopy, dedupe_rules: rules });
                  }
                }}
              />
              </LockedNodeOverlay>
            </TabsContent>
          )}
          <TabsContent value="filtering" className="flex-1 overflow-auto px-4 py-4 mt-4">
            <LockedNodeOverlay managedByTemplateId={isTemplateLocked ? node.managed_by_template_id : undefined} unlocked={false}>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium mb-2">Data Filters</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Filter records in this node based on column values. Only records matching these conditions will be included when this node is queried.
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  Note: Filters are applied after records have been processed according to the Sources & Columns configuration.
                </p>
              </div>
              {workingCopy && (() => {
                // Get current data_whitelist
                const currentWhitelist: DataWhitelist = workingCopy.data_whitelist || {
                  entries: [],
                  logic_gate: 'AND',
                };

                // Create column options from this node's fields (excluding FOREIGN_KEY)
                const filterColumnOptions: ComboboxOption[] = (workingCopy.fields || [])
                  .filter(field => field.type !== 'FOREIGN_KEY')
                  .map(field => ({
                    value: `${workingCopy.id}:${field.id || field.name}`,
                    label: field.label || field.name || 'Unnamed Column',
                    description: field.description,
                  }));

                // Convert to FilterEntry format
                const filterEntries: FilterEntry[] = currentWhitelist.entries.map(entry => ({
                  columnId: `${entry.pipeline_node_id}:${entry.field_id}`,
                  comparator: entry.comparator,
                  value: entry.value,
                }));

                return (
                  <FilterTable
                    filters={filterEntries}
                    onChange={(newFilters) => {
                      // Convert FilterEntry back to DataWhitelistEntry
                      const newEntries: DataWhitelistEntry[] = newFilters.map(f => {
                        const [pipelineNodeId, fieldId] = f.columnId.split(':');
                        return {
                          pipeline_node_id: pipelineNodeId || workingCopy.id,
                          field_id: fieldId || '',
                          comparator: f.comparator,
                          value: f.value,
                        };
                      });

                      const newWhitelist: DataWhitelist = {
                        ...currentWhitelist,
                        entries: newEntries,
                      };

                      setWorkingCopy({
                        ...workingCopy,
                        data_whitelist: newWhitelist.entries.length > 0 ? newWhitelist : undefined,
                      });
                    }}
                    columnOptions={filterColumnOptions}
                    columnLabel="Column"
                    showComparator={true}
                    valueMode="text"
                    emptyMessage="No filters configured. Click the + button to add a filter."
                  />
                );
              })()}
            </div>
            </LockedNodeOverlay>
          </TabsContent>
          <TabsContent value="advanced" className="flex-1 overflow-auto px-4 py-4 mt-4">
            <div className="space-y-8">
              {/* Template Management Section */}
              {node.managed_by_template_id && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Lock className="h-4 w-4" />
                      Template
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      This node is managed by the template <strong>{templateName || 'Unknown'}</strong>.
                      Template-managed nodes are locked by default and will be updated when the template is upgraded.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="unlock-node"
                        checked={workingCopy?.unlocked || false}
                        disabled={!isInDraftMode}
                        onCheckedChange={(checked) => {
                          if (workingCopy) {
                            setWorkingCopy({ ...workingCopy, unlocked: checked });
                          }
                        }}
                      />
                      <Label htmlFor="unlock-node">Unlock this node</Label>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {workingCopy?.unlocked
                        ? 'This node is unlocked. It will not be overwritten by template upgrades.'
                        : 'This node is locked. It will be overwritten when the template is upgraded. Unlock to make custom changes.'}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Incremental Build & Snapshots — locked when template-locked */}
              <LockedNodeOverlay managedByTemplateId={isTemplateLocked ? node.managed_by_template_id : undefined} unlocked={false}>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Incremental Build</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Enable incremental builds to only process new or updated records based on a timestamp column.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {workingCopy && (
                    <>
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="incremental-build"
                          checked={workingCopy.incremental_build || false}
                          onCheckedChange={(checked) => {
                            setWorkingCopy({
                              ...workingCopy,
                              incremental_build: checked,
                              incremental_timestamp_field_id: checked ? workingCopy.incremental_timestamp_field_id : undefined,
                            });
                          }}
                        />
                        <Label htmlFor="incremental-build">Enable incremental build</Label>
                      </div>
                      {workingCopy.incremental_build && (
                        <div className="space-y-2">
                          <Label>Timestamp Column</Label>
                          <p className="text-xs text-muted-foreground mb-2">
                            Select the datetime column used to identify new or updated records.
                          </p>
                          <Combobox
                            options={(workingCopy.fields || [])
                              .filter(field => isSourceNode || field.type === 'DATETIME' || field.type === 'DATETIME_TZ')
                              .map(field => ({
                                value: field.id || field.name || '',
                                label: field.label || field.name || 'Unnamed Column',
                              }))}
                            value={workingCopy.incremental_timestamp_field_id || ''}
                            onValueChange={(value) => {
                              setWorkingCopy({
                                ...workingCopy,
                                incremental_timestamp_field_id: value || undefined,
                              });
                            }}
                            placeholder="Select timestamp column..."
                            emptyText="No columns available."
                          />
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Snapshots Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Snapshots</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Enable snapshots to store the current state of this table daily. This allows you to track changes to records over time.
                  </p>
                </CardHeader>
                <CardContent>
                  {workingCopy && (
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="include-in-snapshots"
                        checked={workingCopy.include_in_snapshots || false}
                        onCheckedChange={(checked) => {
                          setWorkingCopy({
                            ...workingCopy,
                            include_in_snapshots: checked,
                          });
                        }}
                      />
                      <Label htmlFor="include-in-snapshots">Enable snapshots</Label>
                    </div>
                  )}
                </CardContent>
              </Card>
              </LockedNodeOverlay>
            </div>
          </TabsContent>
          {isEntityNode && (
            <TabsContent value="relationships" className="flex-1 overflow-auto px-4 py-4 mt-4">
              <LockedNodeOverlay managedByTemplateId={isTemplateLocked ? node.managed_by_template_id : undefined} unlocked={false}>
                {id && <RelationshipsTab nodeId={id} />}
              </LockedNodeOverlay>
            </TabsContent>
          )}
          <TabsContent value="connections" className="flex-1 overflow-hidden px-4 py-4 mt-4">
            <LockedNodeOverlay managedByTemplateId={isTemplateLocked ? node.managed_by_template_id : undefined} unlocked={false}>
              {id && <ConnectionsTab nodeId={id} />}
            </LockedNodeOverlay>
          </TabsContent>
          <TabsContent value="data" className="flex-1 overflow-hidden px-4 py-4 mt-4">
            {id && (
              <NodeDataTable
                pipelineNodeId={id}
                search=""
                defaultSort=""
              />
            )}
          </TabsContent>
        </Tabs>
      );
    }
  };

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between w-full px-6 py-4 border-b border-border bg-background flex-shrink-0 z-10">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          {/* Breadcrumbs */}
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/develop">Develop</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={parentPath}>{parentLabel}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{node.label || node.name || 'Node'}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          {/* Page Title */}
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-foreground">{node.label || node.name || 'Node'}</h1>
            {node.build_error && (
              <Badge
                variant="destructive"
                className="cursor-pointer"
                onClick={() => setBuildErrorDialogOpen(true)}
              >
                ERROR
              </Badge>
            )}
          </div>
        </div>
        {/* Save and Rebuild Buttons */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRebuildClick}
            disabled={!node}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Rebuild
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={!node}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setCopyDialogOpen(true)}>
                <Copy className="h-4 w-4 mr-2" />
                Copy Model
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setDeleteDialogOpen(true)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Model
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="default"
            size="sm"
            onClick={handleSave}
            disabled={saving || !workingCopy || !isDirty || isTemplateLocked}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="flex-1 min-h-0 overflow-hidden flex">
        {/* Left Sidebar */}
        <NodeDetailSidebar
          availableNodes={availableNodes}
          isLoadingNodes={isLoadingNodes}
          selectedNodeId={selectedNodeId}
          onNodeSelect={setSelectedNodeId}
          selectedNode={selectedNode}
          sidebarTitle="Quick Nav"
          currentNodeId={id}
          onInsertRef={isCustomNode && activeTab === 'sql' ? (refName) => insertAtCursor(`{{ref('${refName}')}}`) : undefined}
          onInsertColumn={isCustomNode && activeTab === 'sql' ? (columnName) => {
            // Check if column name is SQL-safe (alphanumeric and underscores only, not starting with number)
            const isSqlSafe = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(columnName);
            const formatted = isSqlSafe ? columnName.toUpperCase() : `"${columnName}"`;
            insertAtCursor(formatted);
          } : undefined}
          onViewStats={handleSidebarViewStats}
          onViewLineage={handleSidebarViewLineage}
        />

        {/* Main Content Area */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {renderConfigurationContent()}
        </div>
      </div>

      {/* Column Configuration Dialog */}
      <EditColumnDialog
        open={fieldConfigDialogOpen}
        onOpenChange={(open) => {
          setFieldConfigDialogOpen(open);
          if (!open) setOriginalEditingFieldName(null);
        }}
        editingField={editingField}
        initialFormData={initialFieldFormData}
        initialRelationshipData={initialRelationshipData}
        originalEditingFieldName={originalEditingFieldName}
        currentNodeId={id}
        upstreamNodes={upstreamNodes}
        allNodes={allNodesData?.records || []}
        currentNodeFields={workingCopy?.fields as PipelineNodeField[] | undefined}
        groupingBehavior={workingCopy?.grouping_behavior}
        mappedFieldComboboxOptions={mappedFieldComboboxOptions}
        parentNodeOptions={parentNodeOptions}
        foreignKeyRelationshipOptions={foreignKeyRelationshipOptions}
        sensors={sensors}
        getRelationship={getRelationship}
        pendingRelationships={pendingRelationships}
        relationshipsToSave={relationshipsToSave}
        onSave={({ fieldFormData, relationshipFormData }) => {
          if (!editingField || !workingCopy) return;

          const oldFieldName = editingField.name || editingField.label || '';
          const newFieldName = fieldFormData.name;

          const updatedFields = workingCopy.fields?.map((f) => {
            const matchById = f.id && editingField.id && f.id === editingField.id;
            const matchByName = f.name && editingField.name && f.name === editingField.name;
            if (matchById || matchByName) {
              const updatedField: any = {
                ...f,
                name: fieldFormData.name,
                label: fieldFormData.name,
                description: fieldFormData.description,
                type: fieldFormData.type,
                map_options: fieldFormData.map_options,
                transformer: fieldFormData.transformer || undefined,
                custom_transform_sql: fieldFormData.custom_transform_sql || undefined,
                custom_sql: fieldFormData.custom_sql || undefined,
                translations: fieldFormData.translations && fieldFormData.translations.length > 0
                  ? fieldFormData.translations
                  : undefined,
                part_of_composite_key: workingCopy.grouping_behavior === 'STACK'
                  ? false
                  : (fieldFormData.part_of_composite_key || false),
                merge_behavior: fieldFormData.merge_behavior || undefined,
              };

              if (fieldFormData.type === 'FOREIGN_KEY' && relationshipFormData?.id) {
                updatedField.relationship_id = relationshipFormData.id;
                // Queue the existing relationship for save so edits to name/description/lookups persist
                setRelationshipsToSave((prev) => {
                  const next = new Map(prev);
                  next.set(relationshipFormData.id!, {
                    ...relationshipFormData,
                    child_node_id: id,
                    child_foreign_key_name: relationshipFormData.child_foreign_key_name || updatedField.name || '',
                  });
                  return next;
                });
              }

              if (fieldFormData.type === 'FOREIGN_KEY' && relationshipFormData && !relationshipFormData.id) {
                const parentNode = allNodesData?.records?.find((n) => n.id === relationshipFormData.parent_node_id);
                const generatedFkName = generateForeignKeyName(
                  relationshipFormData.name || null,
                  parentNode?.name || null
                );

                const oldFieldKey = updatedField.id || updatedField.name || '';
                updatedField.name = generatedFkName;
                updatedField.label = generatedFkName;
                const newFieldKey = updatedField.id || generatedFkName;

                const updatedRelationshipFormData = {
                  ...relationshipFormData,
                  child_foreign_key_name: generatedFkName,
                };

                if (newFieldKey) {
                  setPendingRelationships((prev) => {
                    const next = new Map(prev);
                    if (oldFieldKey && oldFieldKey !== newFieldKey) {
                      next.delete(oldFieldKey);
                    }
                    next.set(newFieldKey, updatedRelationshipFormData);
                    return next;
                  });
                }
              }

              if (fieldFormData.type === 'DENORMALIZED' && (f as PipelineNodeField).relationship_id) {
                updatedField.relationship_id = (f as PipelineNodeField).relationship_id;

                if (oldFieldName !== newFieldName && (f as PipelineNodeField).relationship_id && getRelationship) {
                  const relationship = getRelationship((f as PipelineNodeField).relationship_id!);
                  if (relationship) {
                    const updatedDenormalizedFields = (relationship.denormalized_fields || []).map((df) => {
                      if (df.output_field_name === oldFieldName) {
                        return { ...df, output_field_name: newFieldName };
                      }
                      return df;
                    });

                    const updatedRelationship = {
                      ...relationship,
                      denormalized_fields: updatedDenormalizedFields,
                    };

                    setRelationshipsToSave((prev) => {
                      const next = new Map(prev);
                      next.set(relationship.id, updatedRelationship);
                      return next;
                    });
                  }
                }
              }

              return updatedField;
            }
            return f;
          });

          setWorkingCopy({ ...workingCopy, fields: updatedFields });
          setFieldConfigDialogOpen(false);
          setOriginalEditingFieldName(null);
        }}
        onDenormalizedRelationshipChange={({ oldRelationshipId, newRelationshipId, fieldName, editingField: ef }) => {
          if (oldRelationshipId && oldRelationshipId !== newRelationshipId && getRelationship) {
            const oldRelationship = relationshipsToSave.has(oldRelationshipId)
              ? relationshipsToSave.get(oldRelationshipId)!
              : getRelationship(oldRelationshipId);
            if (oldRelationship) {
              const updatedOldRel = removeDenormalizedFieldFromRelationship(
                oldRelationship,
                fieldName
              );
              setRelationshipsToSave((prev) => {
                const next = new Map(prev);
                next.set(oldRelationshipId, updatedOldRel);
                return next;
              });
            }
          }

          if (workingCopy) {
            const updatedFields = workingCopy.fields?.map((f) => {
              if (f.id === ef.id || f.name === ef.name) {
                return { ...f, relationship_id: newRelationshipId || undefined };
              }
              return f;
            });
            setWorkingCopy({ ...workingCopy, fields: updatedFields });

            const updatedField = updatedFields?.find(
              (f) => f.id === ef.id || f.name === ef.name
            );
            if (updatedField) {
              setEditingField(updatedField);
            }
          }
        }}
        onDenormalizedParentColumnChange={({ relationshipId, parentFieldId, fieldName, originalFieldName }) => {
          const isPending = isPendingRelationshipId(relationshipId);
          const fieldKey = getPendingFieldKey(relationshipId);

          let rel: Partial<PipelineNodeRelationship> | undefined;
          if (isPending && fieldKey) {
            rel = pendingRelationships.get(fieldKey);
          } else if (relationshipsToSave.has(relationshipId)) {
            rel = relationshipsToSave.get(relationshipId);
          } else if (getRelationship) {
            rel = getRelationship(relationshipId);
          }
          if (!rel) return;

          const existingFields = rel.denormalized_fields || [];
          const withoutOldEntry = existingFields.filter(
            (df) => df.output_field_name !== originalFieldName
          );
          const updatedDenormalizedFields = parentFieldId
            ? [...withoutOldEntry, { parent_field_id: parentFieldId, output_field_name: fieldName }]
            : withoutOldEntry;
          const updatedRelationship: Partial<PipelineNodeRelationship> = {
            ...rel,
            denormalized_fields: updatedDenormalizedFields,
          };

          if (isPending && fieldKey) {
            setPendingRelationships((prev) => {
              const next = new Map(prev);
              next.set(fieldKey, updatedRelationship);
              return next;
            });
          } else {
            setRelationshipsToSave((prev) => {
              const next = new Map(prev);
              next.set(relationshipId, updatedRelationship);
              return next;
            });
          }

          if (fieldName !== originalFieldName) {
            setOriginalEditingFieldName(fieldName);
          }
        }}
      />

      {/* Delete Column Confirmation Dialog */}
      <Dialog open={deleteFieldDialogOpen} onOpenChange={setDeleteFieldDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Column</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the column "{fieldToDelete ? getFieldName(fieldToDelete, getRelationship, getNodeName, id || undefined) : 'Unnamed Column'}"? This will only be permanent if you save your changes.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteFieldDialogOpen(false);
                setFieldToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!fieldToDelete || !workingCopy) {
                  setDeleteFieldDialogOpen(false);
                  setFieldToDelete(null);
                  return;
                }

                // Use utility function to determine what to delete and update
                const deleteResult = getDeleteFieldResult(
                  fieldToDelete as PipelineNodeField,
                  (workingCopy.fields || []) as PipelineNodeField[],
                  getRelationship,
                  relationshipsToSave
                );

                // Remove all fields (the FOREIGN_KEY/DENORMALIZED and any associated fields)
                const updatedFields = removeFieldsFromList(
                  (workingCopy.fields || []) as PipelineNodeField[],
                  deleteResult.fieldsToRemove
                );
                setWorkingCopy({ ...workingCopy, fields: updatedFields });

                // If this is a FOREIGN_KEY field, flag the relationship for deletion
                if (deleteResult.relationshipIdToDelete) {
                  setRelationshipsToDelete((prev) => new Set(prev).add(deleteResult.relationshipIdToDelete!));

                  // Also remove from relationshipsToSave if it was queued there
                  setRelationshipsToSave((prev) => {
                    const next = new Map(prev);
                    next.delete(deleteResult.relationshipIdToDelete!);
                    return next;
                  });
                } else if (deleteResult.relationshipToUpdate) {
                  // For DENORMALIZED fields, queue the updated relationship for save
                  const relationshipId = (fieldToDelete as PipelineNodeField).relationship_id!;
                  setRelationshipsToSave((prev) => {
                    const next = new Map(prev);
                    next.set(relationshipId, deleteResult.relationshipToUpdate!);
                    return next;
                  });
                }

                setDeleteFieldDialogOpen(false);
                setFieldToDelete(null);
                const deletedCount = deleteResult.fieldsToRemove.length;
                toastService.success(
                  deletedCount > 1
                    ? `Deleted ${deletedCount} columns (including ${deletedCount - 1} denormalized column${deletedCount > 2 ? 's' : ''})`
                    : 'Column deleted'
                );
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sidebar Column Stats Dialog */}
      <Dialog open={sidebarStatsDialogOpen} onOpenChange={setSidebarStatsDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Column Statistics</DialogTitle>
            <DialogDescription>
              {sidebarStatsField && (() => {
                const node = allNodesData?.records?.find((n: PipelineNode) => n.id === sidebarStatsNodeId);
                const nodeName = node?.label || node?.name || 'Unknown Node';
                const fieldName = sidebarStatsField.label || sidebarStatsField.name || 'Unknown Column';
                return `Statistics for "${fieldName}" from "${nodeName}"`;
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4 py-4">
            {sidebarLoadingStats ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-muted-foreground">Loading statistics...</div>
              </div>
            ) : sidebarColumnStats ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Density</div>
                    <div className="text-2xl font-semibold">
                      {formatPercentage(sidebarColumnStats.density)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Density measures how many records have a value in this column.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Uniqueness</div>
                    <div className="text-2xl font-semibold">
                      {formatPercentage(sidebarColumnStats.uniqueness)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Uniqueness measures how many distinct values exist in this column.
                    </p>
                  </div>
                </div>

                {sidebarColumnStats.distinct_values !== undefined && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Distinct Values</div>
                    <div className="text-lg">
                      {sidebarColumnStats.distinct_values.toLocaleString()}
                    </div>
                  </div>
                )}

                {sidebarColumnStats.empty_values !== undefined && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Empty Values</div>
                    <div className="text-lg">
                      {sidebarColumnStats.empty_values.toLocaleString()}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="text-sm font-medium">Most Common Values</div>
                  {sidebarColumnDistributionQuery.isLoading ? (
                    <div className="text-sm text-muted-foreground py-2">Loading distribution...</div>
                  ) : sidebarColumnDistributionQuery.data && sidebarColumnDistributionQuery.data.length > 0 ? (
                    <div className="space-y-2">
                      {sidebarColumnDistributionQuery.data.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 bg-muted rounded"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-mono truncate" title={String(item.value)}>
                              {item.value === null || item.value === '' ? (
                                <span className="text-muted-foreground italic">(empty)</span>
                              ) : (
                                String(item.value)
                              )}
                            </div>
                          </div>
                          <div className="ml-4 text-sm font-semibold">
                            {item.total.toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground py-2">
                      No distribution data available
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground py-4">
                No statistics available for this column.
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSidebarStatsDialogOpen(false);
                setSidebarStatsNodeId('');
                setSidebarStatsField(null);
                setSidebarColumnStats(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sidebar Column Lineage Dialog */}
      <FullScreenTakeover
        show={sidebarLineageDialogOpen}
        onHide={() => {
          setSidebarLineageDialogOpen(false);
          setSidebarLineageNodeId('');
          setSidebarLineageField(null);
          setSidebarColumnLineage(null);
        }}
        noCloseButton={true}
        title={
          sidebarLineageField
            ? (() => {
                const node = allNodesData?.records?.find((n: PipelineNode) => n.id === sidebarLineageNodeId);
                const nodeName = node?.label || node?.name || 'Unknown Node';
                const fieldName = sidebarLineageField.label || sidebarLineageField.name || 'Unknown Column';
                return `Column Lineage: "${fieldName}" from "${nodeName}"`;
              })()
            : 'Column Lineage'
        }
      >
        <div className="h-full">
          {sidebarLoadingLineage ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-sm text-muted-foreground">Loading lineage...</div>
            </div>
          ) : sidebarColumnLineage ? (
            <div className="h-full w-full">
              <ColumnLineageGraph
                graph={sidebarColumnLineage}
                selectedNodeId={sidebarLineageNodeId}
                selectedFieldId={sidebarLineageField?.id}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-sm text-muted-foreground">No lineage data available</div>
            </div>
          )}
        </div>
      </FullScreenTakeover>

      {/* Unsaved Changes Warning Dialog */}
      <Dialog open={unsavedChangesWarningOpen} onOpenChange={setUnsavedChangesWarningOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Unsaved Changes
            </DialogTitle>
            <DialogDescription>
              You have unsaved changes. Would you like to save your changes before rebuilding?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setUnsavedChangesWarningOpen(false)}>
              Go Back
            </Button>
            <Button onClick={handleSaveThenRebuild} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save & Rebuild'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rebuild Modal */}
      {node && (
        <RebuildModal
          node={node}
          open={rebuildModalOpen}
          onOpenChange={setRebuildModalOpen}
        />
      )}

      {/* Delete Model Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) setDeleteConfirmName('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Model
            </DialogTitle>
            <DialogDescription className="space-y-3">
              <p>
                Are you sure you want to delete <strong>{node?.label || node?.name}</strong>?
              </p>
              <p className="text-destructive font-medium">
                Warning: Deleting this model may cause downstream issues for any models, metrics, or analyses that depend on it.
              </p>
              <p>
                To confirm, type the model name below:
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="py-4">
            <Input
              placeholder={node?.label || node?.name || 'Model name'}
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteModel}
              disabled={isDeleting || deleteConfirmName !== (node?.label || node?.name)}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Confirm Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Model Dialog */}
      <Dialog open={copyDialogOpen} onOpenChange={(open) => {
        setCopyDialogOpen(open);
        if (!open) setCopyName('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy Model</DialogTitle>
            <DialogDescription>
              Create a copy of <strong>{node?.label || node?.name}</strong> with a new name.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="py-4">
            <Label htmlFor="copy-name" className="mb-2 block">New Model Name</Label>
            <Input
              id="copy-name"
              placeholder="Enter name for the copy"
              value={copyName}
              onChange={(e) => setCopyName(e.target.value)}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCopyModel}
              disabled={isCopying || !copyName.trim()}
            >
              {isCopying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Copying...
                </>
              ) : (
                'Create Copy'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Build Error Dialog */}
      <Dialog open={buildErrorDialogOpen} onOpenChange={setBuildErrorDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Build Error
            </DialogTitle>
            <DialogDescription>
              The last build of this model failed with the following error:
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="py-4">
            <pre className="bg-muted p-4 rounded-md text-sm overflow-auto max-h-96 whitespace-pre-wrap">
              {node?.build_error}
            </pre>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBuildErrorDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


